import { useEffect, useRef, useState } from 'react';
import { escucharTabla, permitirEscritura, supabase } from './supabase';

// ── SUPABASE-BACKED PERSISTENCE ──────────────────────────────────────────────
// Estado local-first: localStorage da lectura instantánea y respaldo offline,
// mientras que Supabase guarda una copia remota en una tabla key-value
// `app_state(key text primary key, value jsonb, updated_at timestamptz)`.
// Si Supabase no está disponible (sin tabla / sin red), la app sigue
// funcionando sólo con localStorage — toda la sincronización es tolerante a
// fallos y nunca lanza errores hacia la UI.

const TABLE = 'app_state';

// Evita reintentos ruidosos si la tabla remota no existe / no es accesible.
let remoteDisabled = false;
async function remoteLoad<T>(key: string): Promise<T | undefined> {
  if (remoteDisabled) return undefined;
  try {
    const {
      data,
      error
    } = await supabase.from(TABLE).select('value').eq('key', key).maybeSingle();
    if (error) {
      // 42P01 = tabla inexistente; desactivamos remoto para no insistir.
      if ((error as {
        code?: string;
      }).code === '42P01') remoteDisabled = true;
      return undefined;
    }
    return data?.value as T ?? undefined;
  } catch {
    return undefined;
  }
}
async function remoteSave<T>(key: string, value: T): Promise<void> {
  if (remoteDisabled) return;
  // Freno contra bucles: aquí vivía el que se comió el plan de tiempo real.
  // Ver `permitirEscritura` en supabase.ts.
  if (!permitirEscritura(`${TABLE}:${key}`)) return;
  try {
    const {
      error
    } = await supabase.from(TABLE).upsert({
      key,
      value,
      updated_at: new Date().toISOString()
    }, {
      onConflict: 'key'
    });
    if (error && (error as {
      code?: string;
    }).code === '42P01') {
      remoteDisabled = true;
    }
  } catch {

    /* offline u otro fallo: ignoramos, localStorage ya tiene la copia */}
}
/**
 * Estado guardado, con copia en Supabase.
 *
 * `soloLectura` lo deja leer del servidor pero NO escribir nunca. Es para las
 * claves de módulos retirados (empleados, mediciones, equipos): siguen
 * alimentando pantallas que las leen —Alertas usa las mediciones— pero ya no
 * hay ninguna pantalla que las cambie, así que escribirlas solo puede salir
 * mal. Y salió mal: `app_state` acumuló 461.432 actualizaciones, el 99,7% de
 * todo lo que se ha escrito en la base, contra unas 1.200 de TODAS las demás
 * tablas juntas. Cada una de esas escrituras es un aviso de tiempo real por
 * pestaña conectada, y eso fue lo que se comió el plan.
 */
export function useStoredState<T>(key: string, initial: T, opciones?: {
  soloLectura?: boolean;
}): [T, React.Dispatch<React.SetStateAction<T>>] {
  const soloLectura = !!opciones?.soloLectura;
  const [value, setValue] = useState<T>(() => {
    if (typeof window === 'undefined') return initial;
    try {
      const raw = window.localStorage.getItem(key);
      return raw ? JSON.parse(raw) as T : initial;
    } catch {
      return initial;
    }
  });

  // Última copia que sabemos que está en el servidor, ya serializada. Sirve
  // para no volver a escribir lo mismo: es lo que rompe el bucle de más abajo.
  const ultimoRemoto = useRef<string | null>(null);

  // Hidratar desde Supabase una sola vez al montar (sin pisar ediciones locales).
  const hydrated = useRef(false);
  useEffect(() => {
    let cancelled = false;
    remoteLoad<T>(key).then(remote => {
      if (cancelled || remote === undefined) {
        hydrated.current = true;
        return;
      }
      // Sólo adoptamos el valor remoto si el usuario aún no ha tocado nada.
      if (!hydrated.current) {
        // Se anota como "esto ya está en el servidor": si no, al entrar cada
        // pestaña reescribiría de vuelta lo que acaba de leer, y eso son
        // cuatro avisos de tiempo real por carga y por usuario, para nada.
        ultimoRemoto.current = JSON.stringify(remote);
        setValue(remote);
        try {
          window.localStorage.setItem(key, ultimoRemoto.current);
        } catch {

          /* ignore */}
      }
      hydrated.current = true;
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  // Tiempo real: `app_state` es una tabla de clave/valor compartida, así que
  // llegan avisos de TODAS las claves y hay que quedarse solo con la propia.
  // Sirve, por ejemplo, para que una alerta que uno da por resuelta desaparezca
  // en las demás pantallas.
  useEffect(() => {
    return escucharTabla(TABLE, ({
      tipo,
      fila
    }) => {
      if (tipo === 'DELETE' || fila?.key !== key) return;
      // ── ESTO ES LO QUE CAUSABA EL BUCLE ───────────────────────────────────
      // El aviso llega, se guarda en el estado, y el efecto de abajo lo
      // volvía a mandar al servidor. Ese guardado disparaba otro aviso, que
      // volvía a entrar aquí, y así sin parar. Medido: una sola notificación
      // dejaba la app escribiendo 2,5 veces por segundo para siempre, o sea
      // más de un millón de mensajes de tiempo real al día POR PESTAÑA
      // abierta, sin que nadie tocara nada.
      //
      // React no lo frenaba solo porque el valor que llega es un objeto
      // recién parseado: aunque diga exactamente lo mismo, es una referencia
      // nueva, así que el efecto se volvía a disparar.
      //
      // Se apunta lo que acaba de llegar como "esto ya está en el servidor",
      // y el efecto de guardar lo compara antes de escribir.
      ultimoRemoto.current = JSON.stringify(fila.value);
      setValue(fila.value as T);
      try {
        window.localStorage.setItem(key, ultimoRemoto.current);
      } catch {

        /* ignore */}
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  // Persistir cambios en localStorage + Supabase (con debounce).
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const serializado = JSON.stringify(value);
    try {
      window.localStorage.setItem(key, serializado);
    } catch {

      /* ignore quota errors */}
    // Clave de solo lectura: se guarda en este navegador y ahí se queda.
    if (soloLectura) return;
    // No empujamos a remoto hasta haber intentado hidratar (evita carrera).
    if (!hydrated.current) return;
    // Ya está así en el servidor: escribirlo otra vez no cambia nada y sí
    // genera un aviso de tiempo real para todos los que estén conectados.
    if (ultimoRemoto.current === serializado) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      ultimoRemoto.current = serializado;
      remoteSave(key, value);
    }, 400);
    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
  }, [key, value]);
  return [value, setValue];
}
// ── SESIÓN DE USUARIO (solo local, con expiración) ───────────────────────────
// A diferencia de useStoredState, la sesión NUNCA se sincroniza con la tabla
// remota `app_state`: esa tabla es compartida por clave y sin distinción de
// dispositivo/usuario, así que si la sesión viajara por ahí, el último login
// de cualquier persona quedaría disponible para cualquier otro dispositivo
// que abriera la app (justo el problema de "entro desde el celular y ya
// estoy adentro"). La sesión vive únicamente en el localStorage del propio
// dispositivo/navegador, y además expira sola:
//  - por inactividad (nadie toca la app por SESSION_INACTIVITY_MS), y
//  - de forma absoluta (aunque haya actividad) a las SESSION_ABSOLUTE_MS.
const SESSION_KEY = 'getr.session';
const SESSION_INACTIVITY_MS = 30 * 60 * 1000; // 30 min sin uso -> fuera
const SESSION_ABSOLUTE_MS = 12 * 60 * 60 * 1000; // 12 h desde el login -> fuera

interface StoredSession<T> {
  usuario: T;
  loginAt: number;
  lastActivity: number;
}
function readSession<T>(): StoredSession<T> | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as StoredSession<T>;
  } catch {
    return null;
  }
}
function sessionExpired<T>(s: StoredSession<T>): boolean {
  const now = Date.now();
  return now - s.lastActivity > SESSION_INACTIVITY_MS || now - s.loginAt > SESSION_ABSOLUTE_MS;
}
export function useSessionState<T>(): [T | null, (usuario: T | null) => void] {
  const [usuario, setUsuario] = useState<T | null>(() => {
    const s = readSession<T>();
    if (!s) return null;
    if (sessionExpired(s)) {
      try {
        window.localStorage.removeItem(SESSION_KEY);
      } catch {

        /* ignore */}
      return null;
    }
    return s.usuario;
  });
  const setSession = (next: T | null) => {
    setUsuario(next);
    if (typeof window === 'undefined') return;
    try {
      if (next === null) {
        window.localStorage.removeItem(SESSION_KEY);
        return;
      }
      const existing = readSession<T>();
      const now = Date.now();
      const loginAt = existing ? existing.loginAt : now;
      window.localStorage.setItem(SESSION_KEY, JSON.stringify({
        usuario: next,
        loginAt,
        lastActivity: now
      }));
    } catch {

      /* ignore quota errors */}
  };

  // Mientras haya sesión: refresca "última actividad" con la interacción del
  // usuario, revisa periódicamente si expiró (inactividad o tiempo absoluto)
  // y se mantiene sincronizado si la sesión se cierra desde otra pestaña.
  useEffect(() => {
    if (typeof window === 'undefined' || !usuario) return;
    const touch = () => {
      const s = readSession<T>();
      if (!s) return;
      s.lastActivity = Date.now();
      try {
        window.localStorage.setItem(SESSION_KEY, JSON.stringify(s));
      } catch {

        /* ignore */}
    };
    const checkExpiry = () => {
      const s = readSession<T>();
      if (!s || sessionExpired(s)) {
        try {
          window.localStorage.removeItem(SESSION_KEY);
        } catch {

          /* ignore */}
        setUsuario(null);
      }
    };
    const onStorage = (e: StorageEvent) => {
      if (e.key === SESSION_KEY && e.newValue === null) setUsuario(null);
    };
    const activityEvents = ['click', 'keydown', 'mousemove', 'scroll', 'touchstart'];
    activityEvents.forEach(ev => window.addEventListener(ev, touch, {
      passive: true
    }));
    window.addEventListener('storage', onStorage);
    const interval = setInterval(checkExpiry, 60 * 1000);
    return () => {
      activityEvents.forEach(ev => window.removeEventListener(ev, touch));
      window.removeEventListener('storage', onStorage);
      clearInterval(interval);
    };
  }, [usuario]);
  return [usuario, setSession];
}
export function csvCell(value: unknown): string {
  const str = String(value ?? '');
  if (/[",\n]/.test(str)) return `"${str.replace(/"/g, '""')}"`;
  return str;
}
export function downloadText(filename: string, contents: string, mime: string) {
  if (typeof window === 'undefined') return;
  const blob = new Blob([contents], {
    type: mime
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 0);
}